'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { configureRequestHooks, SESSION_EXPIRED_MESSAGE, SESSION_EXPIRED_TITLE } from '@shared/services/requestHooks';
import { auth } from '@/lib/firebase/config';
import { onAuthStateChanged, signOut } from '@/lib/firebase/auth';
import { apiService } from '@/services/api';
import { FirebaseUser } from '@/types/firebase';

interface User {
  uid: string;
  id: string;
  email: string;
  displayName: string;
  emailVerified: boolean;
  photoURL?: string;
  disabled?: boolean;
  dob?: string | Date;
  joydropReason?: string;
  bio?: string;
  dots?: number;
  rewardEligible?: boolean;
  rewardsClaimed?: {
    claimedAt: string;
    thirdDayReward: boolean;
  };
  username?: string;
  longestStreak?: number;
  onboardingCompleted?: boolean;
  passwordChangedAt?: string;
  currentStreak?: number;
  lastStreakDate?: string;
  loginCount?: number;
  birthday?: string;
  aboutMe?: string;
  hasUnreadNotifications?: boolean;
  termsAcceptedAt?: string;
  termsVersion?: string;
  signupSource?: string;
}

interface AuthContextType {
  user: User | null;
  currentUser: User | null;
  username: string;
  token: string | null;
  authToken: string | null;
  dots: number;
  photoURL: string | '';
  login: (user: User, token: string) => void;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  checkEmailVerification: () => Promise<boolean>;
  refreshIdToken: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
  updateUserEmailVerified: (uid: string, emailVerified: boolean) => Promise<void>;
  fetchUserDots: () => Promise<void>;
  updateUserDots: (dotsToAdd: number) => Promise<void>;
  getUsername: () => Promise<string | null>;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [dots, setDots] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Load user data from localStorage on mount FIRST (before Firebase auth initializes)
  useEffect(() => {
    if (!hasInitialized) {
      const savedUser = localStorage.getItem('user');
      const savedToken = localStorage.getItem('token');

      if (savedUser && savedToken) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          setToken(savedToken);

          // Set dots from saved user data - preserve them until API call completes
          if (userData.dots !== undefined && userData.dots !== null) {
            setDots(userData.dots);
          }
        } catch (error) {
          console.error('Error parsing saved user data:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      }
      setHasInitialized(true);
    }
  }, [hasInitialized]);

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(async (firebaseUser: FirebaseUser) => {
      if (firebaseUser) {
        // User is signed in
        // Get existing dots from localStorage to preserve them during API call
        let currentDots = 0;
        try {
          const savedUser = localStorage.getItem('user');
          if (savedUser) {
            const parsedUser = JSON.parse(savedUser);
            if (parsedUser.dots !== undefined && parsedUser.dots !== null) {
              currentDots = parsedUser.dots;
            }
          }
        } catch {
          // Ignore localStorage parse errors
        }

        const userData: User = {
          uid: firebaseUser.uid,
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || '',
          emailVerified: firebaseUser.emailVerified,
        };

        // Get the ID token
        const idToken = await firebaseUser.getIdToken();
        setToken(idToken);
        localStorage.setItem('token', idToken);

        // Fetch complete user data including dots from API (requires verified email on backend)
        try {
          if (!firebaseUser.emailVerified) {
            const offlineUser: User = {
              ...userData,
              dots: currentDots,
            };
            setUser(offlineUser);
            setDots(currentDots);
            localStorage.setItem('user', JSON.stringify(offlineUser));
          } else {
          const apiResponse = await apiService.getUserById(firebaseUser.uid, idToken);
          if (apiResponse.data) {
            const completeUserData: User = {
              uid: apiResponse.data.uid || firebaseUser.uid,
              id: apiResponse.data.id || firebaseUser.uid,
              email: apiResponse.data.email || firebaseUser.email || '',
              displayName: apiResponse.data.displayName || firebaseUser.displayName || '',
              emailVerified:
                Boolean(firebaseUser.emailVerified) ||
                Boolean((apiResponse.data as { emailVerified?: boolean }).emailVerified),
              photoURL: apiResponse.data.photoURL,
              disabled: apiResponse.data.disabled || false,
              dob: apiResponse.data.dob || '',
              joydropReason: apiResponse.data.joydropReason,
              bio: apiResponse.data.bio,
              // Use API dots if available, otherwise preserve current dots from localStorage
              dots: apiResponse.data.dots !== undefined && apiResponse.data.dots !== null
                ? apiResponse.data.dots
                : currentDots,
              rewardEligible: apiResponse.data.rewardEligible || false,
              username: apiResponse.data.username,
              longestStreak: apiResponse.data.longestStreak,
              onboardingCompleted: apiResponse.data.onboardingCompleted,
              currentStreak: apiResponse.data.currentStreak,
              lastStreakDate: apiResponse.data.lastStreakDate,
              loginCount: apiResponse.data.loginCount,
              aboutMe: apiResponse.data.bio,
              hasUnreadNotifications: apiResponse.data.hasUnreadNotifications || false,
              termsAcceptedAt: apiResponse.data.termsAcceptedAt,
              termsVersion: apiResponse.data.termsVersion,
              signupSource: apiResponse.data.signupSource,
            };
            setUser(completeUserData);
            setDots(completeUserData.dots ?? currentDots);
            // Save complete user data to localStorage
            localStorage.setItem('user', JSON.stringify(completeUserData));
          } else {
            // Fallback: merge with existing user data from localStorage to preserve dots
            try {
              const savedUser = localStorage.getItem('user');
              const existingUser = savedUser ? JSON.parse(savedUser) : null;
              const mergedUserData = {
                ...userData,
                ...(existingUser || {}),
                uid: firebaseUser.uid,
                id: firebaseUser.uid,
                email: firebaseUser.email || '',
                displayName: firebaseUser.displayName || '',
                emailVerified: firebaseUser.emailVerified,
                dots: currentDots,
              };
              setUser(mergedUserData);
              setDots(currentDots);
              localStorage.setItem('user', JSON.stringify(mergedUserData));
            } catch {
              // If localStorage parse fails, just use basic user data
              setUser({ ...userData, dots: currentDots });
              setDots(currentDots);
              localStorage.setItem('user', JSON.stringify({ ...userData, dots: currentDots }));
            }
          }
          }
        } catch (error: any) {
          // Don't log 404s as errors, as they are expected for new users.
          const isNotFound =
            error?.message?.includes('status 404') || error?.message?.includes('404');

          if (isNotFound) {
            console.debug('User profile not found in DB yet (expected for new users)');
          } else {
            // API unreachable is common in local dev; we still fall back to Firebase + localStorage.
            console.warn(
              'Could not load user profile from API (using Firebase + cached data):',
              error instanceof Error ? error.message : error,
            );
          }
          // Fallback: merge with existing user data from localStorage to preserve dots
          try {
            const savedUser = localStorage.getItem('user');
            const existingUser = savedUser ? JSON.parse(savedUser) : null;
            const mergedUserData = {
              ...userData,
              ...(existingUser || {}),
              uid: firebaseUser.uid,
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || '',
              emailVerified: firebaseUser.emailVerified,
              dots: currentDots,
            };
            setUser(mergedUserData);
            setDots(currentDots);
            localStorage.setItem('user', JSON.stringify(mergedUserData));
          } catch {
            // If localStorage parse fails, just use basic user data
            setUser({ ...userData, dots: currentDots });
            setDots(currentDots);
            localStorage.setItem('user', JSON.stringify({ ...userData, dots: currentDots }));
          }
        }

      } else {
        // User is signed out
        setUser(null);
        setToken(null);
        setDots(0);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []); // Empty dependency array - onAuthStateChanged should only be set up once

  // Fetch user dots from the API (only when explicitly called, not automatically)
  const fetchUserDots = useCallback(async (): Promise<void> => {
    if (user?.uid && token) {
      try {
        const userDots = await apiService.getUserDots(user.uid, token);
        const dotsValue = userDots || 0;
        setDots(dotsValue);

        // Update user object with dots
        const updatedUser = user ? { ...user, dots: dotsValue } : null;
        setUser(updatedUser);

        // Update localStorage with dots
        if (updatedUser) {
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      } catch (error) {
        console.error('Failed to fetch user dots:', error);
        // Don't reset dots to 0 on error - preserve current value
      }
    }
  }, [user, token]);

  const login = (userData: User, userToken: string) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', userToken);
  };

  const logout = async (): Promise<void> => {
    try {
      // Sign out from Firebase first
      await signOut();

      // Clear local state and localStorage
      setUser(null);
      setToken(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');

      // Redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if Firebase logout fails, clear local state
      setUser(null);
      setToken(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');

      // Still redirect to login page
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
  };

  // Check if current user's email is verified
  const checkEmailVerification = async (): Promise<boolean> => {
    if (user && auth.currentUser) {
      await auth.currentUser.reload(); // Refresh user data
      const isVerified = auth.currentUser.emailVerified;

      // Update local state
      setUser(prev => prev ? { ...prev, emailVerified: isVerified } : null);

      return isVerified;
    }
    return false;
  };

  // Forces a brand-new ID token from Firebase, rather than reusing the cached one.
  // Needed right after email verification is confirmed: the token issued at sign-up
  // time still carries email_verified:false baked into its claims, even after
  // auth.currentUser.reload() updates the *local* emailVerified flag above. Any
  // authenticated request made with the stale token (e.g. accepting updated terms)
  // gets rejected by the backend with 401 until a fresh token is fetched — which is
  // exactly why logging out and back in "fixes" it, since sign-in always issues a
  // new token anyway.
  // Keep the login token fresh. Firebase tokens last one hour; before this,
  // a tab left open over an hour sent an expired token and got a 401.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const save = (t: string) => {
      setToken(t);
      try {
        localStorage.setItem('token', t);
      } catch {
        /* ignore */
      }
    };

    // 1. Whenever Firebase issues a new token, use it.
    const unsubscribeToken = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) return;
      try {
        save(await firebaseUser.getIdToken());
      } catch {
        /* the next check retries */
      }
    });

    // getIdToken() returns the current token, or a new one if it's close to expiring.
    const check = async () => {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) return;
      try {
        save(await firebaseUser.getIdToken());
      } catch {
        /* offline; the next check retries */
      }
    };

    // 2. Coming back to the tab.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);

    // 3. A backstop while the tab stays open.
    const interval = window.setInterval(check, 10 * 60 * 1000);

    return () => {
      unsubscribeToken();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      window.clearInterval(interval);
    };
  }, []);

  const refreshIdToken = async (): Promise<string | null> => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return null;
    try {
      const newToken = await firebaseUser.getIdToken(true);
      setToken(newToken);
      if (typeof window !== 'undefined') {
        localStorage.setItem('token', newToken);
      }
      return newToken;
    } catch (error) {
      console.error('Failed to refresh ID token:', error);
      return null;
    }
  };

  // Every API call retries once with a fresh token after a 401. If that still
  // fails, the session really ended: say so plainly and go to login.
  // Re-registered each render so it always uses the latest functions.
  useEffect(() => {
    configureRequestHooks({
      refreshToken: refreshIdToken,
      onSessionExpired: () => {
        if (typeof window !== 'undefined') window.alert(`${SESSION_EXPIRED_TITLE} ${SESSION_EXPIRED_MESSAGE}`);
        logout();
      },
    });
  });

  // Force refresh user data
  const refreshUser = async (): Promise<void> => {
    if (auth.currentUser && token) {
      // Preserve current dots in case API call fails
      const currentDots = dots || (user?.dots ?? 0);

      try {
        await auth.currentUser.reload();
        const firebaseUser = auth.currentUser;

        if (!firebaseUser.emailVerified) {
          const preserved: User = {
            ...(user || {
              uid: firebaseUser.uid,
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              displayName: firebaseUser.displayName || '',
              emailVerified: false,
            }),
            uid: firebaseUser.uid,
            id: firebaseUser.uid,
            email: firebaseUser.email || user?.email || '',
            displayName: firebaseUser.displayName || user?.displayName || '',
            emailVerified: false,
            dots: currentDots,
          };
          setUser(preserved);
          setDots(currentDots);
          localStorage.setItem('user', JSON.stringify(preserved));
          return;
        }

        // Fetch complete user data from API
        const apiResponse = await apiService.getUserById(firebaseUser.uid, token);

        if (apiResponse.data) {
          const userData: User = {
            uid: apiResponse.data.uid || firebaseUser.uid,
            id: apiResponse.data.id || firebaseUser.uid,
            email: apiResponse.data.email,
            displayName: apiResponse.data.displayName,
            emailVerified:
              Boolean(firebaseUser.emailVerified) ||
              Boolean((apiResponse.data as { emailVerified?: boolean }).emailVerified),
            photoURL: apiResponse.data.photoURL,
            disabled: apiResponse.data.disabled || false,
            dob: apiResponse.data.dob || '',
            joydropReason: apiResponse.data.joydropReason,
            bio: apiResponse.data.bio,
            // Use API dots if available, otherwise preserve current dots
            dots: apiResponse.data.dots !== undefined && apiResponse.data.dots !== null
              ? apiResponse.data.dots
              : currentDots,
            rewardEligible: apiResponse.data.rewardEligible || false,
            username: apiResponse.data.username,
            longestStreak: apiResponse.data.longestStreak,
            onboardingCompleted: apiResponse.data.onboardingCompleted,
            currentStreak: apiResponse.data.currentStreak,
            lastStreakDate: apiResponse.data.lastStreakDate,
            loginCount: apiResponse.data.loginCount,
            aboutMe: apiResponse.data.bio,
            hasUnreadNotifications: apiResponse.data.hasUnreadNotifications || false,
            termsAcceptedAt: apiResponse.data.termsAcceptedAt,
            termsVersion: apiResponse.data.termsVersion,
          };

          setUser(userData);
          setDots(userData.dots ?? currentDots);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      } catch (error) {
        console.error('Failed to refresh user data:', error);
        // Fallback: preserve existing user data and dots instead of resetting
        if (user) {
          // Keep existing user data, just update Firebase fields
          const firebaseUser = auth.currentUser;
          const preservedUserData: User = {
            ...user,
            uid: firebaseUser.uid,
            id: firebaseUser.uid,
            email: firebaseUser.email || user.email || '',
            displayName: firebaseUser.displayName || user.displayName || '',
            emailVerified: firebaseUser.emailVerified,
            photoURL: firebaseUser.photoURL || user.photoURL,
            dots: currentDots, // Preserve dots
          };

          setUser(preservedUserData);
          setDots(currentDots);
          localStorage.setItem('user', JSON.stringify(preservedUserData));
        }
      }
    }
  };

  // Update user's email verification status in Firestore
  const updateUserEmailVerified = async (uid: string, emailVerified: boolean): Promise<void> => {
    try {
      // Call your backend API to update the user's emailVerified status
      await apiService.updateUserEmailVerified(uid, emailVerified, token);
      console.log('User email verification status updated in Firestore');
    } catch (error) {
      console.error('Failed to update user email verification status:', error);
      throw error;
    }
  };

  // Update user dots (add or subtract)
  const updateUserDots = async (dotsToAdd: number): Promise<void> => {
    if (user?.uid && token) {
      try {
        // Call API to update dots
        await apiService.updateUserDots(user.uid, dotsToAdd, 'manual_update', token);

        // Update local state
        const newDots = dots + dotsToAdd;
        setDots(newDots);

        // Update user object
        const updatedUser = user ? { ...user, dots: newDots } : null;
        setUser(updatedUser);

        // Update localStorage with new dots
        if (updatedUser) {
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      } catch (error) {
        console.error('Failed to update user dots:', error);
      }
    }
  };

  // Update user data
  const updateUser = (userData: Partial<User>): void => {
    setUser(prev => prev ? { ...prev, ...userData } : null);
  };

  // Get username for the current user
  const getUsername = async (): Promise<string | null> => {
    if (user?.uid && token) {
      try {
        // First check if we already have username in local state
        if (user.username) {
          return user.username;
        }

        // Fetch user data from API to get username
        const userData = await apiService.getUserById(user.uid, token);

        // Update local user state with username
        if (userData.data) {
          const username = userData.data.username || userData.data.displayName || userData.data.email.split('@')[0];
          setUser(prev => prev ? { ...prev, username } : null);
          return username;
        }

        return null;
      } catch (error) {
        console.error('Failed to fetch username:', error);
        return null;
      }
    }
    return null;
  };

  const value: AuthContextType = {
    user,
    currentUser: user, // Add currentUser alias
    username: user?.username || user?.displayName || user?.email?.split('@')[0] || '',
    token,
    authToken: token, // Add authToken alias
    dots,
    photoURL: user?.photoURL || '',
    login,
    logout,
    isAuthenticated: Boolean(user && token && user.emailVerified),
    isLoading,
    checkEmailVerification,
    refreshIdToken,
    refreshUser,
    fetchUserDots,
    updateUserDots,
    getUsername,
    updateUser,
  };

  if (isLoading) {
    return (
      <div
        suppressHydrationWarning
        className="min-h-screen bg-[#12100F] flex items-center justify-center font-mono"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7B2FFF] mx-auto mb-4"></div>
          <p suppressHydrationWarning className="text-[#F5F0E8] uppercase tracking-widest text-xs">
            loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
